import React, { useState, useMemo, useContext, useCallback, useRef, useEffect } from "react";
import NavBar from "../partials/NavBar";
import { GlobalContext } from "../GlobalContext";
import MissingTerms from "../partials/MissingTerms";

// Cycle order for the per-column role selector.
const ROLES = ["string", "name", "ignore"];
const ROLE_LABEL = { string: "String", name: "Name", ignore: "Ignore" };
const ROLE_HINT = {
  string: "Description / string — auto-translated line-by-line",
  name: "Name — resolved against the client DB (handles (Sealed)/(Time-limited) suffixes)",
  ignore: "Left untouched",
};

const LANGS = [
  { code: "KR", label: "Korean" },
  { code: "EU", label: "English" },
  { code: "RU", label: "Russian" },
];

const PREFS_KEY = "l2bt_prefs";
const loadPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY)) || {};
  } catch (e) {
    return {};
  }
};

// Split pasted spreadsheet text into a grid. Rows by newline, columns by tab.
// (L2 DAT cells store line breaks as the literal two-character "\n", so real
// newlines only ever separate rows here.)
function parseGrid(text) {
  const lines = (text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  // Drop a single trailing empty line from a copy that ended with a newline.
  if (lines.length && lines[lines.length - 1] === "") lines.pop();
  const rows = lines.map((l) => l.split("\t"));
  const cols = rows.reduce((m, r) => Math.max(m, r.length), 0);
  return rows.map((r) => {
    const copy = r.slice();
    while (copy.length < cols) copy.push("");
    return copy;
  });
}

// Guess a role per column: all-numeric -> ignore (likely an id/flag column);
// short single-line text -> name; anything longer or multi-line -> string.
function guessRoles(rows) {
  const cols = rows.reduce((m, r) => Math.max(m, r.length), 0);
  const roles = [];
  for (let c = 0; c < cols; c++) {
    const vals = rows.map((r) => (r[c] || "").trim()).filter(Boolean);
    if (!vals.length) {
      roles.push("ignore");
      continue;
    }
    const allNumeric = vals.every((v) => /^-?\d+$/.test(v));
    if (allNumeric) {
      roles.push("ignore");
      continue;
    }
    const anyMultiline = vals.some((v) => v.includes("\\n") || v.length > 64);
    roles.push(anyMultiline ? "string" : "name");
  }
  return roles;
}

function BatchTranslate() {
  const { username, logout, updateDatabase, toast, socket, isSocketReady } =
    useContext(GlobalContext);

  const prefs0 = useRef(loadPrefs()).current;
  const [raw, setRaw] = useState("");
  const [rows, setRows] = useState([]); // string[][]
  const [colRoles, setColRoles] = useState([]);
  const [inLang, setInLang] = useState(prefs0.inLang || "KR");
  const [outLang, setOutLang] = useState(prefs0.outLang || "EU");
  const [type, setType] = useState(prefs0.type || "item");
  const [result, setResult] = useState(null); // {rows, stats, version}
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null); // {phase, done, total}
  const btToastId = useRef(null);
  const inactivityRef = useRef(null);
  const reqCounterRef = useRef(0);
  const cleanupRef = useRef(null);
  // Remembered column roles, keyed by the grid's column count so different data
  // shapes keep their own layout.
  const rolesByColsRef = useRef(prefs0.rolesByCols || {});

  // ---- row virtualization (only render visible rows; grids can be 100k+ cells) ----
  const scrollRef = useRef(null);
  const [view, setView] = useState({ top: 0, h: 600 });
  const [rowH, setRowH] = useState(31);
  const rowHRef = useRef(0);
  const rafRef = useRef(0);
  const onGridScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const el = scrollRef.current;
      if (el) setView({ top: el.scrollTop, h: el.clientHeight });
    });
  }, []);
  // Measure the real row height once so the spacer padding matches exactly.
  const measureRow = useCallback((el) => {
    if (el && !rowHRef.current) {
      const h = el.getBoundingClientRect().height;
      if (h > 0) {
        rowHRef.current = h;
        setRowH(h);
      }
    }
  }, []);
  // Capture the real container height once the grid mounts / data changes, so the
  // initial (pre-scroll) window renders enough rows to fill tall screens.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) setView({ top: el.scrollTop, h: el.clientHeight });
  }, [rows, result]);

  const savePrefs = useCallback((patch) => {
    const next = { ...loadPrefs(), ...patch };
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(next));
    } catch (e) {
      /* ignore quota / privacy-mode errors */
    }
  }, []);

  // Persist language/type selections so they stick between sessions.
  useEffect(() => {
    savePrefs({ inLang, outLang, type });
  }, [inLang, outLang, type, savePrefs]);

  // Tear down any live progress listener/timer if the page unmounts mid-run.
  useEffect(() => () => {
    if (cleanupRef.current) cleanupRef.current();
  }, []);

  const btToast = useCallback(
    (kind, msg) => {
      const fn = kind === "error" ? toast.error : kind === "success" ? toast.success : toast.info;
      if (btToastId.current != null) toast.dismiss(btToastId.current);
      btToastId.current = fn(msg, { position: "bottom-right", autoClose: 2500 });
    },
    [toast]
  );

  const applyParsed = useCallback((text) => {
    setRaw(text);
    const grid = parseGrid(text);
    const cols = grid.reduce((m, r) => Math.max(m, r.length), 0);
    // Reuse the roles you last set for a grid with this many columns; otherwise
    // fall back to the auto-guess.
    const saved = rolesByColsRef.current[cols];
    const roles = Array.isArray(saved) && saved.length === cols ? saved.slice() : guessRoles(grid);
    setRows(grid);
    setColRoles(roles);
    setResult(null);
    // Reset the scroll window for the new grid.
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setView((v) => ({ ...v, top: 0 }));
  }, []);

  const onPaste = (e) => {
    const text = e.clipboardData.getData("text/plain");
    if (text) {
      e.preventDefault();
      applyParsed(text);
    }
  };

  const cycleRole = (c) => {
    setColRoles((prev) => {
      const next = prev.slice();
      const cur = next[c] || "string";
      next[c] = ROLES[(ROLES.indexOf(cur) + 1) % ROLES.length];
      // Remember this layout for grids with the same column count.
      rolesByColsRef.current = { ...rolesByColsRef.current, [next.length]: next };
      savePrefs({ rolesByCols: rolesByColsRef.current });
      return next;
    });
  };

  const cellCount = useMemo(
    () => rows.reduce((n, r) => n + r.length, 0),
    [rows]
  );

  const doTranslate = () => {
    if (!rows.length) {
      btToast("error", "Paste some spreadsheet data first.");
      return;
    }
    if (!socket || !isSocketReady) {
      btToast("error", "Server offline — cannot translate.");
      return;
    }
    if (inLang === outLang) {
      btToast("error", "Input and output languages are the same.");
      return;
    }
    const requestId = `bt-${Date.now()}-${++reqCounterRef.current}`;
    setLoading(true);
    setResult(null);
    setProgress({ phase: "start", done: 0, total: rows.length });

    let settled = false;

    const onProgress = (p) => {
      if (!p || p.requestId !== requestId) return;
      setProgress(p);
      armTimeout(); // fresh progress means the server is alive; reset the watchdog
    };

    const cleanup = () => {
      clearTimeout(inactivityRef.current);
      if (socket) socket.off("batchProgress", onProgress);
      cleanupRef.current = null;
    };
    cleanupRef.current = cleanup;

    // Inactivity watchdog: only fail if the server goes silent (no progress and
    // no final answer) for a stretch — a long-but-working batch won't trip it.
    function armTimeout() {
      clearTimeout(inactivityRef.current);
      inactivityRef.current = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        setLoading(false);
        setProgress(null);
        btToast("error", "Server went silent for 60s — it may have stalled or disconnected.");
      }, 60000);
    }

    socket.on("batchProgress", onProgress);
    armTimeout();

    socket.emit(
      "batchTranslate",
      { rows, colRoles, inLang, outLang, type, requestId },
      (resp) => {
        if (settled) return;
        settled = true;
        cleanup();
        setLoading(false);
        setProgress(null);
        if (!resp) {
          btToast("error", "No response from the server.");
          return;
        }
        if (resp.error) {
          btToast("error", "Translation failed: " + resp.error);
          return;
        }
        setResult(resp);
        const s = resp.stats || {};
        const failed = (s.nameFailed || 0) + (s.stringFailed || 0);
        btToast(
          failed ? "info" : "success",
          failed
            ? `Done — ${failed} cell${failed === 1 ? "" : "s"} need review.`
            : "Done — all cells translated."
        );
      }
    );
  };

  // Flush the server's term cache so terms edited directly in the DB are used
  // immediately (otherwise cached hits live up to 5 min).
  const clearTermCache = () => {
    if (!socket || !isSocketReady) {
      btToast("error", "Server offline.");
      return;
    }
    socket.emit("clearTranslationCache", {}, (resp) => {
      if (resp && resp.ok) btToast("success", "Term cache cleared — edited terms are live.");
      else btToast("error", "Could not clear the term cache.");
    });
  };

  const copyResult = async () => {
    if (!result) return;
    const tsv = result.rows.map((r) => r.map((cell) => cell.t).join("\t")).join("\n");
    try {
      await navigator.clipboard.writeText(tsv);
      btToast("success", "Copied result — paste into your sheet.");
    } catch (e) {
      btToast("error", "Clipboard blocked by the browser.");
    }
  };

  const langOptions = (exclude) =>
    LANGS.map((l) => (
      <option key={l.code} value={l.code} disabled={l.code === exclude}>
        {l.label}
      </option>
    ));

  return (
    <div>
      <NavBar username={username} onLogout={logout} updateDatabase={updateDatabase} />
      <div className="bt-page">
        <h2 className="bt-title">Batch Translator</h2>
        <p className="bt-sub">
          Paste spreadsheet data (tab-separated). Strings are auto-translated line-by-line; name
          columns are resolved against the latest client version's data.
        </p>

        <div className="bt-controls">
          <label className="bt-field">
            <span>From</span>
            <select value={inLang} onChange={(e) => setInLang(e.target.value)}>
              {langOptions(outLang)}
            </select>
          </label>
          <label className="bt-field">
            <span>To</span>
            <select value={outLang} onChange={(e) => setOutLang(e.target.value)}>
              {langOptions(inLang)}
            </select>
          </label>
          <label className="bt-field">
            <span>Data type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="item">Item</option>
              <option value="skill">Skill (% → %%)</option>
            </select>
          </label>
          <button className="bt-primary" onClick={doTranslate} disabled={loading}>
            {loading ? "Translating…" : "Translate"}
          </button>
          <button
            className="bt-secondary"
            onClick={clearTermCache}
            title="Flush the server's term cache so terms you edited directly in the DB are used right away"
          >
            ↻ Refresh term cache
          </button>
          {result && (
            <button className="bt-secondary" onClick={copyResult}>
              Copy result (TSV)
            </button>
          )}
        </div>

        {loading && progress && (
          <div className="bt-progress">
            {(() => {
              const total = progress.total || 0;
              const doneN = progress.done || 0;
              const pct = total ? Math.round((doneN / total) * 100) : 0;
              const label =
                progress.phase === "names"
                  ? "Resolving names against the client database…"
                  : total
                  ? `Translating rows… ${doneN}/${total} (${pct}%)`
                  : "Working…";
              return (
                <>
                  <div className="bt-progress-label">{label}</div>
                  <div className="bt-progress-track">
                    <div
                      className={"bt-progress-fill" + (progress.phase === "names" ? " bt-progress-indet" : "")}
                      style={{ width: progress.phase === "names" ? "100%" : `${pct}%` }}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {!rows.length ? (
          <textarea
            className="bt-paste"
            placeholder="Paste rows here (Ctrl+V). Columns separated by tabs, one item/skill per row."
            value={raw}
            onPaste={onPaste}
            onChange={(e) => applyParsed(e.target.value)}
          />
        ) : (
          <>
            <div className="bt-meta">
              {rows.length} row{rows.length === 1 ? "" : "s"} · {colRoles.length} column
              {colRoles.length === 1 ? "" : "s"} · {cellCount} cells
              {result && result.version ? ` · matched vs v${result.version}` : ""}
              <button
                className="bt-link"
                onClick={() => {
                  setRows([]);
                  setColRoles([]);
                  setRaw("");
                  setResult(null);
                }}
              >
                Clear
              </button>
            </div>
            <p className="bt-hint">
              Click a column header to set its role: <b>String</b> (translate), <b>Name</b> (DB
              lookup) or <b>Ignore</b>.
            </p>
            {(() => {
              const displayRows = result ? result.rows : rows;
              const total = displayRows.length;
              const OVERSCAN = 8;
              const start = Math.max(0, Math.floor(view.top / rowH) - OVERSCAN);
              const end = Math.min(total, start + Math.ceil(view.h / rowH) + OVERSCAN * 2);
              const topPad = start * rowH;
              const bottomPad = Math.max(0, (total - end) * rowH);
              const colCount = colRoles.length;
              return (
                <div className="bt-gridwrap" ref={scrollRef} onScroll={onGridScroll}>
                  <table className="bt-grid">
                    <colgroup>
                      {colRoles.map((_, c) => (
                        <col key={c} style={{ width: 220 }} />
                      ))}
                    </colgroup>
                    <thead>
                      <tr>
                        {colRoles.map((role, c) => (
                          <th key={c}>
                            <button
                              className={`bt-role bt-role-${role}`}
                              onClick={() => cycleRole(c)}
                              title={ROLE_HINT[role]}
                            >
                              {ROLE_LABEL[role]}
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {topPad > 0 && (
                        <tr aria-hidden="true">
                          <td colSpan={colCount} style={{ height: topPad, padding: 0, border: 0 }} />
                        </tr>
                      )}
                      {displayRows.slice(start, end).map((row, i) => {
                        const r = start + i;
                        return (
                          <tr key={r} ref={i === 0 ? measureRow : undefined}>
                            {row.map((cell, c) => {
                              const isResult = !!result;
                              const text = isResult ? cell.t : cell;
                              const status = isResult ? cell.s : null;
                              return (
                                <td
                                  key={c}
                                  className={
                                    "bt-cell" +
                                    (colRoles[c] === "ignore" ? " bt-cell-ignore" : "") +
                                    (status === "fail" ? " bt-cell-fail" : "") +
                                    (status === "partial" ? " bt-cell-partial" : "")
                                  }
                                  title={text == null ? "" : String(text)}
                                >
                                  {text}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {bottomPad > 0 && (
                        <tr aria-hidden="true">
                          <td colSpan={colCount} style={{ height: bottomPad, padding: 0, border: 0 }} />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            <p className="bt-hint">
              {(result ? result.rows : rows).length} rows · only visible rows are rendered (scroll to
              load more).
            </p>
            {result && result.stats && (
              <div className="bt-stats">
                Names: {result.stats.name} ({result.stats.nameFailed} unresolved) · Strings:{" "}
                {result.stats.string} ({result.stats.stringFailed} unresolved) · Ignored:{" "}
                {result.stats.ignored}
              </div>
            )}
            {result && (
              <MissingTerms
                missing={result.missing}
                resetKey={result}
                notify={btToast}
                onRerun={doTranslate}
                rerunLabel="Re-run translation"
                rerunTitle="Translate again using the terms you just saved"
                rerunDisabled={loading}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default BatchTranslate;
